import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Ship, Bike, Car, Trash2, Mail, MessageCircle, Filter,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import useConfirmDialog from "@/lib/useConfirmDialog";

const SERVICE_META = {
  boat_charter: { label: "Private Boat Charter", Icon: Ship },
  quad_scooter: { label: "Quad & Scooter Rental", Icon: Bike },
  rent_a_car:   { label: "Premium Rent-a-Car",   Icon: Car },
};

const STATUS_OPTIONS = ["new", "contacted", "booked", "closed"];
const STATUS_COLOR = {
  new:       "bg-[#C5A059]/15 text-[#C5A059] border-[#C5A059]/30",
  contacted: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  booked:    "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  closed:    "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
};

function inCurrentMonth(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  return d.getUTCFullYear() === now.getUTCFullYear() && d.getUTCMonth() === now.getUTCMonth();
}

export default function AdminInquiries() {
  const navigate = useNavigate();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterService, setFilterService] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [notesDraft, setNotesDraft] = useState({}); // { [id]: string }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/experience-inquiries");
      setInquiries(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("juke_admin_token");
        navigate("/admin");
      } else {
        toast.error("Could not load inquiries");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("juke_admin_token")) {
      navigate("/admin");
      return;
    }
    load();
  }, [load, navigate]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/experience-inquiries/${id}`, { status });
      setInquiries((list) => list.map((i) => (i.id === id ? { ...i, ...data } : i)));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const saveNotes = async (id) => {
    const admin_notes = notesDraft[id] ?? "";
    try {
      const { data } = await api.patch(`/admin/experience-inquiries/${id}`, { admin_notes });
      setInquiries((list) => list.map((i) => (i.id === id ? { ...i, ...data } : i)));
      toast.success("Notes saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save notes");
    }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete this inquiry?",
      description: "The customer's message and admin notes will be permanently removed. This cannot be undone.",
      confirmLabel: "Delete inquiry",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/experience-inquiries/${id}`);
      setInquiries((list) => list.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = useMemo(() => {
    return inquiries.filter((i) => {
      if (filterService !== "all" && i.service_type !== filterService) return false;
      if (filterStatus !== "all" && (i.status || "new") !== filterStatus) return false;
      return true;
    });
  }, [inquiries, filterService, filterStatus]);

  // KPI aggregates
  const kpis = useMemo(() => {
    const total = inquiries.length;
    const monthAll = inquiries.filter((i) => inCurrentMonth(i.created_at)).length;
    const perService = {};
    for (const code of Object.keys(SERVICE_META)) {
      perService[code] = {
        total: 0, month: 0, new: 0, contacted: 0, booked: 0, closed: 0,
      };
    }
    for (const i of inquiries) {
      const s = perService[i.service_type];
      if (!s) continue;
      s.total += 1;
      if (inCurrentMonth(i.created_at)) s.month += 1;
      const st = i.status || "new";
      if (st in s) s[st] += 1;
    }
    return { total, monthAll, perService };
  }, [inquiries]);

  return (
    <div data-testid="admin-inquiries-page" className="min-h-screen bg-[#050505] text-zinc-100">
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link
            to="/admin/dashboard"
            data-testid="inquiries-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-[#C5A059] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </Link>
          <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Experience Inquiries</p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* KPI strip */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
          <KpiCard label="All time" value={kpis.total} eyebrow="Total inquiries" gold />
          <KpiCard label="This month" value={kpis.monthAll} eyebrow="New this month" />
          {Object.entries(SERVICE_META).map(([code, meta]) => (
            <KpiCard
              key={code}
              testid={`kpi-${code}`}
              icon={<meta.Icon className="w-4 h-4" />}
              label={meta.label}
              value={kpis.perService[code].month}
              eyebrow={`This month · ${kpis.perService[code].total} all time`}
            />
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center mb-5 text-xs">
          <span className="inline-flex items-center gap-1 uppercase tracking-[0.2em] text-zinc-500 mr-1">
            <Filter className="w-3.5 h-3.5" /> Service
          </span>
          {["all", ...Object.keys(SERVICE_META)].map((s) => (
            <button
              key={`svc-${s}`}
              data-testid={`filter-service-${s}`}
              onClick={() => setFilterService(s)}
              className={`px-3 h-9 rounded-full border uppercase tracking-[0.15em] transition-colors ${
                filterService === s
                  ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/5"
                  : "border-white/10 text-zinc-400 hover:text-zinc-100"
              }`}
            >
              {s === "all" ? "All" : SERVICE_META[s].label.split(" ")[0]}
            </button>
          ))}
          <span className="inline-flex items-center gap-1 uppercase tracking-[0.2em] text-zinc-500 ml-4 mr-1">
            <Filter className="w-3.5 h-3.5" /> Status
          </span>
          {["all", ...STATUS_OPTIONS].map((s) => (
            <button
              key={`st-${s}`}
              data-testid={`filter-status-${s}`}
              onClick={() => setFilterStatus(s)}
              className={`px-3 h-9 rounded-full border uppercase tracking-[0.15em] transition-colors ${
                filterStatus === s
                  ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/5"
                  : "border-white/10 text-zinc-400 hover:text-zinc-100"
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="rounded-xl border border-white/10 bg-[#0d0d0d] overflow-hidden">
          <div className="hidden lg:grid grid-cols-12 px-6 py-4 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/10">
            <div className="col-span-3">Guest</div>
            <div className="col-span-3">Service</div>
            <div className="col-span-2">Requested date</div>
            <div className="col-span-2">Received</div>
            <div className="col-span-2 text-right">Status</div>
          </div>

          {loading ? (
            <p className="p-8 text-center text-zinc-500 text-sm">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="p-8 text-center text-zinc-500 text-sm">No inquiries match the current filters.</p>
          ) : (
            filtered.map((i) => {
              const meta = SERVICE_META[i.service_type] || {};
              const status = i.status || "new";
              const isOpen = expandedId === i.id;
              return (
                <div
                  key={i.id}
                  data-testid={`inquiry-row-${i.id}`}
                  className="border-b border-white/5 last:border-0"
                >
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setExpandedId(isOpen ? null : i.id);
                      if (!isOpen) setNotesDraft((d) => ({ ...d, [i.id]: i.admin_notes || "" }));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setExpandedId(isOpen ? null : i.id);
                        if (!isOpen) setNotesDraft((d) => ({ ...d, [i.id]: i.admin_notes || "" }));
                      }
                    }}
                    className="w-full px-6 py-4 grid grid-cols-1 lg:grid-cols-12 gap-2 items-center text-left hover:bg-white/[0.02] transition-colors cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-[#C5A059]/60"
                  >
                    <div className="lg:col-span-3">
                      <p className="text-white text-sm font-medium">{i.name}</p>
                      <p className="text-xs text-zinc-500">{i.email}</p>
                    </div>
                    <div className="lg:col-span-3 flex items-center gap-2 text-sm text-zinc-300">
                      {meta.Icon ? <meta.Icon className="w-4 h-4 text-[#C5A059]" /> : null}
                      <span>{meta.label || i.service_type}</span>
                    </div>
                    <div className="lg:col-span-2 text-sm text-zinc-400 tabular-nums">
                      {i.service_date || "—"}
                    </div>
                    <div className="lg:col-span-2 text-sm text-zinc-500">
                      {fmtDateTime(i.created_at)}
                    </div>
                    <div className="lg:col-span-2 flex lg:justify-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <span
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`status-badge-${i.id}`}
                            className={`px-3 py-1 rounded-full border text-[10px] uppercase tracking-[0.2em] cursor-pointer ${STATUS_COLOR[status]}`}
                          >
                            {status}
                          </span>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          onClick={(e) => e.stopPropagation()}
                          className="bg-[#0d0d0d] border-white/10 text-zinc-100"
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <DropdownMenuItem
                              key={s}
                              data-testid={`status-set-${i.id}-${s}`}
                              onClick={() => updateStatus(i.id, s)}
                              className="text-xs uppercase tracking-[0.2em] cursor-pointer"
                            >
                              {s}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="bg-[#080808] border-t border-white/5 px-6 py-5 grid grid-cols-1 lg:grid-cols-3 gap-6">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Contact</p>
                        <div className="space-y-1 text-sm">
                          <p className="text-zinc-200">{i.name}</p>
                          <a href={`mailto:${i.email}`} className="text-zinc-400 hover:text-[#C5A059] inline-flex items-center gap-2">
                            <Mail className="w-3.5 h-3.5" /> {i.email}
                          </a>
                          <a
                            href={`https://wa.me/${(i.whatsapp || "").replace(/[^0-9]/g, "")}`}
                            target="_blank" rel="noreferrer"
                            className="text-zinc-400 hover:text-[#C5A059] inline-flex items-center gap-2 block"
                          >
                            <MessageCircle className="w-3.5 h-3.5" /> {i.whatsapp}
                          </a>
                        </div>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-5 mb-2">Guest message</p>
                        <p className="text-sm text-zinc-300 whitespace-pre-wrap">
                          {i.notes || <span className="text-zinc-600 italic">No notes provided.</span>}
                        </p>
                      </div>

                      <div className="lg:col-span-2">
                        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Admin notes (internal)</p>
                        <Textarea
                          data-testid={`admin-notes-${i.id}`}
                          value={notesDraft[i.id] ?? ""}
                          onChange={(e) => setNotesDraft((d) => ({ ...d, [i.id]: e.target.value }))}
                          rows={5}
                          placeholder="Partner contacted, quote sent, confirmed for X date…"
                          className="bg-zinc-900 border-white/10 text-sm"
                        />
                        <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
                          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                            {i.updated_at ? `Last updated ${fmtDateTime(i.updated_at)}` : "Never updated"}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              data-testid={`save-notes-${i.id}`}
                              onClick={() => saveNotes(i.id)}
                              className="h-9 px-4 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black text-[10px] uppercase tracking-[0.25em] font-semibold"
                            >
                              Save notes
                            </button>
                            <button
                              type="button"
                              data-testid={`delete-inquiry-${i.id}`}
                              onClick={() => remove(i.id)}
                              className="h-9 px-3 rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/10 text-[10px] uppercase tracking-[0.25em] inline-flex items-center gap-2"
                            >
                              <Trash2 className="w-3.5 h-3.5" /> Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </main>
      <ConfirmDialog />
    </div>
  );
}

function KpiCard({ label, value, eyebrow, icon, gold, testid }) {
  return (
    <div
      data-testid={testid || `kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={`rounded-2xl border p-4 ${gold ? "border-[#C5A059]/40 bg-[#C5A059]/5" : "border-white/10 bg-[#0d0d0d]"}`}
    >
      <div className={`text-[10px] uppercase tracking-[0.25em] flex items-center gap-2 ${gold ? "text-[#C5A059]" : "text-zinc-500"}`}>
        {icon} {label}
      </div>
      <p className={`text-3xl font-display mt-1 tabular-nums ${gold ? "text-[#C5A059]" : "text-white"}`}>{value}</p>
      {eyebrow && <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{eyebrow}</p>}
    </div>
  );
}
